import React, { FC } from 'react';
import { reduxForm, Field, getFormValues } from 'redux-form';
import { checkBox, muiCustomSelect, newThemeInput } from '_fields/inputs';
import { lowerCase } from '_fields/normalizers';
import { connect, useStore } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { formValueSelector } from 'redux-form';
import { useForm } from 'react-hook-form';
import { Form } from '@components/shadcn/components/ui/form';
import { RootState } from '@store/reducers';
import { Spinner } from '@components/Spinner/Spinner';
import { Helmet } from 'react-helmet';
import { Text } from 'shared-components';
import { NaicsField } from '@components/Apply/MoreDetailsOnYourBusiness/NaicsField';
import { AddressField } from '@components/Apply/MoreDetailsOnYourBusiness/AddressField';
import withOnSubmit from '@components/Apply/withOnSubmit/withOnSubmit';
import { ApplyWizardSteps, STEP_BUSINESS_MORE_DETAILS } from '@components/ApplyWizard/ApplyWizardSteps';
import { reduxFormZodValidate } from '@utils/reduxFormZodValidate';
import { useApplyWizardContext } from '../../../context/ApplyWizardContext';
import ensureLoggedIn from '../../../hooks/ensureLoggedIn';
import { LoanWizardShell, LoanSubmitButton, getLoanPhaseIndex } from '@components/Apply/WizardShell';

type MoreDetailsOnYourBusinessProps = {
  loanType: string;
  handleSubmit: () => void;
  invalid: boolean;
  years?: number;
  saving?: boolean;
};

const MoreDetailsOnYourBusinessWrapper: FC<MoreDetailsOnYourBusinessProps> = (props) => {
  const auth0 = useAuth0();
  const { currentPath } = useApplyWizardContext();
  const phase = getLoanPhaseIndex(currentPath);

  if (auth0.isLoading) {
    return (
      <LoanWizardShell currentStepIndex={phase}>
        <div className="flex items-center justify-center w-full py-[60px]">
          <Spinner />
        </div>
      </LoanWizardShell>
    );
  }

  return <MoreDetailsOnYourBusinessMainComponent {...props} />;
};

const structure = ['Corporation', 'LLC', 'Partnership', 'Sole Proprietorship'];

const MoreDetailsOnYourBusinessMainComponent: FC<MoreDetailsOnYourBusinessProps> = ({
  handleSubmit,
  invalid,
  saving: savingOnSubmit,
  years,
}) => {
  const { saving, currentPath } = useApplyWizardContext();
  const phase = getLoanPhaseIndex(currentPath);
  const form = useForm();

  const store = useStore();
  const latestValues = getFormValues('application')(store.getState() as RootState) || {};
  const moreYearsValue = years && Number(years) > 20 ? Number(years) : 20;
  const validationOutput = reduxFormZodValidate(ApplyWizardSteps[STEP_BUSINESS_MORE_DETAILS].zodSchema)(latestValues);

  const isSubmitting = !!(saving || savingOnSubmit);

  return (
    <LoanWizardShell currentStepIndex={phase}>
      <Helmet>
        <title>More Details on Your Business</title>
      </Helmet>

      <div className="flex items-center justify-center py-[20px] w-full">
        <Text variant="headline-sm" className="!font-extended !uppercase !text-[16px] !leading-none text-center">
          Business Information
        </Text>
      </div>

      <div className="flex flex-col gap-[10px] items-center w-full">
        <div className="flex items-center justify-center py-[10px] w-full">
          <Text variant="body-general" className="!text-[20px] !leading-[30px] text-center">
            Business Information
          </Text>
        </div>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="w-full">
            <div className="w-full flex flex-col gap-[10px]">
              <div className="w-full max-w-[368px] mx-auto text-left">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  BUSINESS ADDRESS*
                </span>
                <AddressField
                  autoFocus={false}
                  variant="transparent-black"
                  getManualAddressText={(isManualAddress: boolean) => {
                    return isManualAddress ? 'Use Search Tool' : 'Enter Manually';
                  }}
                  toggleManualAddressClassName="uppercase"
                  geoSuggestClassName="!mx-auto"
                  geoSuggestInputClassName="!bg-dawn3"
                  addressContainerProps={{ className: 'mx-auto' }}
                  addressStreetProps={{ className: '!bg-dawn3' }}
                  addressSuiteProps={{ className: '!bg-dawn3' }}
                  addressCityProps={{ className: '!bg-dawn3' }}
                  addressStateProps={{
                    className: '!bg-dawn3',
                    selectTriggerProps: { className: '!bg-dawn3' },
                  }}
                  addressPostalCodeProps={{ className: '!bg-dawn3' }}
                />
                {(latestValues as any).business_city && (validationOutput as any).business_street && (
                  <Text variant="error">Please enter a valid street address</Text>
                )}
              </div>

              <div className="w-full pt-[5px] flex justify-center">
                <Field
                  component={checkBox}
                  id="home_based"
                  name="home_based"
                  variant="transparent-black"
                  stackProps={{ className: 'w-full !my-0' }}
                  labelProps={{
                    className: '!font-standard !text-[14px] !leading-[1.4] !text-charcoal self-center',
                  }}
                  data-testid="consent_check"
                  children={<label htmlFor="home_based">My business is home-based</label>}
                />
              </div>

              <div className="w-full max-w-[368px] mx-auto text-left">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  BUSINESS WEBSITE
                </span>
                <Field
                  data-testid="business-website"
                  name="business_website"
                  component={newThemeInput}
                  type="text"
                  placeholder="Business Website"
                  normalize={lowerCase}
                  format={(v: string) => v?.trim()}
                  className="!bg-dawn3"
                />
              </div>

              <div className="w-full max-w-[368px] mx-auto text-left">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  YEARS IN BUSINESS*
                </span>
                <Field
                  name="business_years"
                  component={muiCustomSelect}
                  label="Years in business*"
                  className="tib-select !bg-dawn3"
                  autoFocus
                  data-testid="business_years"
                  selectValueProps={{ placeholder: 'Years in Business*', className: '!bg-dawn3' }}
                  selectTriggerProps={{ className: '!bg-dawn3' }}
                  valueAsNumber
                  options={[
                    { value: 0, text: 'under 1 year' },
                    { value: 1, text: '1 year' },
                    { value: 2, text: '2 years' },
                    { value: 3, text: '3 years' },
                    { value: 4, text: '4 years' },
                    { value: 5, text: '5 years' },
                    { value: 6, text: '6 years' },
                    { value: 7, text: '7 years' },
                    { value: 8, text: '8 years' },
                    { value: 9, text: '9 years' },
                    { value: 10, text: '10 years' },
                    { value: 11, text: '11 years' },
                    { value: 12, text: '12 years' },
                    { value: 13, text: '13 years' },
                    { value: 14, text: '14 years' },
                    { value: 15, text: '15 years' },
                    { value: 16, text: '16 years' },
                    { value: 17, text: '17 years' },
                    { value: 18, text: '18 years' },
                    { value: 19, text: '19 years' },
                    { value: moreYearsValue, text: '20+ years' },
                  ]}
                />
              </div>

              <div className="w-full max-w-[368px] mx-auto text-left">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  BUSINESS STRUCTURE*
                </span>
                <Field
                  name="business_structure"
                  component={muiCustomSelect}
                  data-testid={`structure_radio_input`}
                  className="!bg-dawn3"
                  selectValueProps={{ placeholder: 'Business Structure*', className: '!bg-dawn3' }}
                  selectTriggerProps={{ className: '!bg-dawn3' }}
                  options={Object.values(structure).map((option) => ({ value: option, text: option }))}
                />
              </div>

              <div className="w-full max-w-[368px] mx-auto text-left">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  INDUSTRY (NAICS)*
                </span>
                <NaicsField />
              </div>

              <div className="flex flex-col items-center justify-center w-full pt-[20px] pb-[10px] px-[10px] gap-[10px]">
                <LoanSubmitButton
                  invalid={
                    invalid || (validationOutput as any).business_street || (validationOutput as any).business_city
                  }
                  saving={isSubmitting}
                  data-testid="info_continue"
                >
                  Continue
                </LoanSubmitButton>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </LoanWizardShell>
  );
};

const MoreDetailsOnYourBusinessWrapper2 = reduxForm<MoreDetailsOnYourBusinessProps, MoreDetailsOnYourBusinessProps>({
  form: 'application',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate: reduxFormZodValidate(ApplyWizardSteps[STEP_BUSINESS_MORE_DETAILS].zodSchema),
})(MoreDetailsOnYourBusinessWrapper);
const appSelector = formValueSelector('application');

const mapStateToProps = (state: RootState) => {
  return {
    years: appSelector(state, 'business_years'),
  };
};

export const MoreDetailsOnYourBusiness = ensureLoggedIn(
  withOnSubmit(connect(mapStateToProps)(MoreDetailsOnYourBusinessWrapper2), STEP_BUSINESS_MORE_DETAILS),
  { requireBusinessId: true }
);
